import { EXTERNAL_PRIMARY_COLORS } from '../../config/deploymentProfile';

/**
 * Color Palettes
 * Centralized color definitions used across all chart types
 */

// Single color options for bar/line charts
export const SINGLE_COLORS = [
  { value: '#4C6EE6', label: 'Medium Blue (AB-600)' },
  { value: '#C44B3D', label: 'Red (RC-700)' },
  { value: '#9E4FA5', label: 'Purple (PC-700)' },
  { value: '#3B5F5C', label: 'Dark Green (GG-700)' },
];

// Sequential palettes (light to dark)
export const BLUES_PALETTE = ['#DBE0F2', '#8FA6F9', '#4C6EE6', '#2D4DB9', '#1E265C'];
export const REDS_PALETTE = ['#FFD9D0', '#FFA18C', '#FF7759', '#CA492D', '#662F24'];
export const GREENS_PALETTE = ['#CFE9B4', '#91D49E', '#5BBF8A', '#357A4D', '#16270D'];

const DISALLOWED_PALETTE_OPTIONS = new Set(['useMultiColor']);

export function isPaletteOptionAccessible(optionKey) {
  return !DISALLOWED_PALETTE_OPTIONS.has(optionKey);
}

export function getAccessiblePaletteOptions(options = [], style) {
  return options.filter((option) => isPaletteOptionAccessible(option.key, style));
}

function normalizeHex(color) {
  if (!color) return null;
  const value = String(color).trim();
  if (!/^#?[0-9a-fA-F]{6}$/.test(value)) return null;
  return (value.startsWith('#') ? value : `#${value}`).toUpperCase();
}

const APPROVED_EXTERNAL_PRIMARIES = EXTERNAL_PRIMARY_COLORS
  .map(({ value }) => normalizeHex(value))
  .filter(Boolean);

/**
 * Interpolate between two hex colors
 */
export function interpolateColor(color1, color2, factor) {
  const hex = (c) => parseInt(c, 16);
  const r1 = hex(color1.slice(1, 3));
  const g1 = hex(color1.slice(3, 5));
  const b1 = hex(color1.slice(5, 7));
  const r2 = hex(color2.slice(1, 3));
  const g2 = hex(color2.slice(3, 5));
  const b2 = hex(color2.slice(5, 7));
  
  const r = Math.round(r1 + (r2 - r1) * factor);
  const g = Math.round(g1 + (g2 - g1) * factor);
  const b = Math.round(b1 + (b2 - b1) * factor);
  
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`.toUpperCase();
}

export function buildPaletteFromPrimary(primaryColor) {
  const color = normalizeHex(primaryColor);
  if (!color) return BLUES_PALETTE;

  return [
    interpolateColor(color, '#FFFFFF', 0.82),
    interpolateColor(color, '#FFFFFF', 0.55),
    color,
    interpolateColor(color, '#000000', 0.18),
    interpolateColor(color, '#000000', 0.34),
  ];
}

export function darkenColor(color, factor = 0.12) {
  const normalized = normalizeHex(color);
  if (!normalized) return color;
  return interpolateColor(normalized, '#000000', factor);
}

export function getSequentialPalette(config = {}, style) {
  if (config.primaryColor) return buildPaletteFromPrimary(config.primaryColor);
  if (style?.locked && style?.paletteSequential?.length) return style.paletteSequential;
  if (config.colorPalette === 'greens') return GREENS_PALETTE;
  if (config.colorPalette === 'reds') return REDS_PALETTE;
  if (config.colorPalette === 'blues') return BLUES_PALETTE;
  return BLUES_PALETTE;
}

export function getPrimaryColorPair(config = {}, style) {
  if (config.primaryColor) {
    const palette = buildPaletteFromPrimary(config.primaryColor);
    return {
      primary: palette[2],
      secondary: palette[4],
      positive: palette[2],
      negative: palette[4],
      highlight: palette[3],
    };
  }

  const palette = style?.locked ? (style?.palette || []) : [];
  return {
    primary: palette[0] || config.color || config.barColor || config.positiveColor || '#4C6EE6',
    secondary: palette[1] || config.lineColor || config.negativeColor || '#C44B3D',
    positive: palette[0] || config.positiveColor || '#4C6EE6',
    negative: palette[1] || config.negativeColor || '#C44B3D',
    highlight: palette[0] || config.highlightColor || config.color || '#4C6EE6',
  };
}

export function getComboColorPair(config = {}, style) {
  const basePair = getPrimaryColorPair(config, style);
  const selectedPrimary = normalizeHex(config.primaryColor);

  if (!selectedPrimary) return basePair;

  const selectedIndex = APPROVED_EXTERNAL_PRIMARIES.indexOf(selectedPrimary);
  if (selectedIndex === -1 || APPROVED_EXTERNAL_PRIMARIES.length < 2) {
    return basePair;
  }

  const secondaryPrimary = APPROVED_EXTERNAL_PRIMARIES[(selectedIndex + 1) % APPROVED_EXTERNAL_PRIMARIES.length];

  return {
    ...basePair,
    primary: selectedPrimary,
    secondary: secondaryPrimary,
  };
}

/**
 * Generate N interpolated colors from a palette
 */
export function interpolatePalette(palette, count) {
  const safeCount = Math.max(1, Number(count) || 1);
  if (count <= 1) return [palette[Math.floor(palette.length / 2)]];

  // Sample within the ramp interior so generated colors avoid harsh endpoints.
  // This keeps ramps softer while still preserving each palette's direction.
  const maxPosition = palette.length - 1;
  const edgeInset = Math.min(0.25, maxPosition * 0.12);
  const start = edgeInset;
  const end = maxPosition - edgeInset;

  const result = [];
  for (let i = 0; i < safeCount; i++) {
    const t = safeCount === 1 ? 0.5 : i / (safeCount - 1);
    const position = start + t * (end - start);
    const lower = Math.floor(position);
    const upper = Math.ceil(position);
    const factor = position - lower;
    
    if (lower === upper) {
      result.push(palette[lower]);
    } else {
      result.push(interpolateColor(palette[lower], palette[upper], factor));
    }
  }
  return result;
}

/**
 * Get colors for chart elements based on config.
 * If style.palette is defined (locked theme), it takes precedence over config toggles.
 */
export function getColors(config, count, style) {
  const targetCount = Math.max(1, Number(count) || Number(config.numCategories) || 1);

  if (config.primaryColor) {
    const normalized = normalizeHex(config.primaryColor) || '#4C6EE6';
    if (targetCount <= 1 || config.externalColorMode === 'single') return Array(targetCount).fill(normalized);
    return interpolatePalette(buildPaletteFromPrimary(config.primaryColor), targetCount);
  }

  // Locked theme palettes override all config-based color options.
  // palette[0] is always the primary accent color (e.g. green).
  // For multi-color use, interpolate only through palette[1..] (the blues range)
  // so we never blend across the accent→blues discontinuity.
  if (style?.locked && style?.palette) {
    if (targetCount <= 1 || config.lockedColorMode === 'single') return [style.palette[0]];
    const rest = style.palette.slice(1);
    return [style.palette[0], ...interpolatePalette(rest, targetCount - 1)];
  }

  if (config.useBluesPalette) {
    return interpolatePalette(BLUES_PALETTE, targetCount);
  } else if (config.useRedsPalette) {
    return interpolatePalette(REDS_PALETTE, targetCount);
  } else if (config.useGreensPalette) {
    return interpolatePalette(GREENS_PALETTE, targetCount);
  }

  // String-based colorPalette (used by radar and other select-based controls)
  if (config.colorPalette === 'blues') return interpolatePalette(BLUES_PALETTE, targetCount);
  if (config.colorPalette === 'reds') return interpolatePalette(REDS_PALETTE, targetCount);
  if (config.colorPalette === 'greens') return interpolatePalette(GREENS_PALETTE, targetCount);
  if (config.colorPalette === 'theme' && style?.palette?.length) {
    if (targetCount <= style.palette.length) return style.palette.slice(0, targetCount);
    return interpolatePalette(style.palette, targetCount);
  }

  // Single color fallback
  return Array(targetCount).fill(config.color || '#4C6EE6');
}

/**
 * Resolve a color for a labeled item, preferring explicit user overrides.
 */
export function resolveColorWithOverrides(autoColor, itemLabel, colorOverrides) {
  return colorOverrides?.[itemLabel] || autoColor;
}

/**
 * Shared colorBy resolution — detects _colorBy field on data, builds color
 * map (respecting overrides), and returns everything the chart preview needs.
 * Returns null when colorBy is not active.
 *
 * Defaults to the Blues palette when no explicit palette is chosen, keeping
 * all bars in the same color family. Users can switch to Reds or Greens.
 */
export function resolveColorByMapping(data, config, theme) {
  if (!Array.isArray(data) || data.length === 0 || data[0]?._colorBy == null) return null;
  const colorByValues = [...new Set(data.map(d => d._colorBy))];
  const n = colorByValues.length;

  const baseColors = getColors(
    config.primaryColor || config.useRedsPalette || config.useGreensPalette || config.colorPalette
      ? config
      : { ...config, useBluesPalette: true },
    n,
    theme,
  );

  const safeColors = baseColors.length >= n
    ? baseColors
    : Array.from({ length: n }, (_, i) => baseColors[i % baseColors.length]);

  const colorMap = Object.fromEntries(
    colorByValues.map((v, i) => [v, resolveColorWithOverrides(safeColors[i], v, config.colorOverrides)])
  );
  const fillFn = (d) => colorMap[d._colorBy];
  const legendItems = colorByValues.map(val => ({ key: val, label: val, color: colorMap[val] }));
  return { colorByValues, colorMap, fillFn, legendItems };
}

// Curated color suggestions for the color picker UI — draws from
// the Blues, Reds, and Greens palettes plus a few neutral extras.
export const COLOR_PICKER_SUGGESTIONS = [
  '#8FA6F9', '#4C6EE6', '#2D4DB9', '#1E265C',
  '#FFA18C', '#FF7759', '#CA492D', '#662F24',
  '#91D49E', '#5BBF8A', '#357A4D', '#16270D',
  '#6B7280', '#1E1E1E',
];

/**
 * Generate matplotlib color code based on config with interpolation
 */
export function generateColorCode(config, varName = 'bar_colors', style) {
  const n = config.numCategories || 5;

  if (config.primaryColor) {
    if (config.externalColorMode === 'single') {
      return `# Single color for all bars\n${varName} = '${normalizeHex(config.primaryColor) || config.primaryColor}'`;
    }
    const colors = getColors(config, n, style);
    return `# Primary-color-derived palette (${n} colors)
${varName} = ${JSON.stringify(colors)}`;
  }
  
  if (config.useBluesPalette) {
    const colors = interpolatePalette(BLUES_PALETTE, n);
    return `# Blues palette (${n} interpolated colors)
${varName} = ${JSON.stringify(colors)}`;
  } else if (config.useRedsPalette) {
    const colors = interpolatePalette(REDS_PALETTE, n);
    return `# Reds palette (${n} interpolated colors)
${varName} = ${JSON.stringify(colors)}`;
  } else if (config.useGreensPalette) {
    const colors = interpolatePalette(GREENS_PALETTE, n);
    return `# Greens palette (${n} interpolated colors)
${varName} = ${JSON.stringify(colors)}`;
  }
  return `# Single color for all bars
${varName} = '${config.color || '#4C6EE6'}'`;
}
